'use client'

import { Eye, X } from 'lucide-react'
import { useTranslation } from '@/lib/i18n'
import { richText } from '@/components/user-portal/learner-i18n'
import { usePlayerAuth } from '../_auth/player-auth-provider'

/**
 * Shown at the top of /play when an admin is impersonating a recipient.
 * No round data persists while this is visible.
 */
export function ImpersonationBanner() {
  const { user } = usePlayerAuth()
  const { t } = useTranslation()
  if (!user?.isImpersonating) return null

  const exit = async () => {
    try {
      await fetch('/api/play/auth/signout', { method: 'POST' })
    } finally {
      // Stay inside the player realm — never bounce into the admin shell
      // (/recipients). The player auth page is the canonical exit target.
      window.location.href = '/play/auth'
    }
  }

  return (
    <div className="bg-amber-500/15 border-b border-amber-500/30 text-amber-950 dark:text-amber-100">
      <div className="mx-auto max-w-7xl px-4 py-2 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Eye className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">
            {richText(t('play.impersonation.message'), {
              mode: <span className="font-medium">{t('play.impersonation.mode')}</span>,
              name: <span className="font-semibold">{user.name || user.email || user.id}</span>,
            })}
          </span>
        </div>
        <button
          onClick={exit}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/20 hover:bg-amber-500/30 transition-colors font-medium flex-shrink-0"
        >
          <X className="h-3.5 w-3.5" />
          {t('play.impersonation.exit')}
        </button>
      </div>
    </div>
  )
}
